import { useEffect, useRef, useState } from 'react';
import { MessageCircle, X, Send, Sparkles, Loader2, Phone, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'ransen_chat_session';

const WELCOME = "Hi! I’m Ransen’s AI assistant. Ask me anything about our services, industries we serve, pricing, or the best way to grow your business. What brings you here today?";
const QUICK_PROMPTS = [
  'What services do you offer?',
  'How much does it cost?',
  'Do you work with SaaS companies?',
  'What is LLM optimization?',
];

function makeSessionId() {
  const stored = typeof window !== 'undefined' && window.localStorage.getItem(STORAGE_KEY);
  if (stored) return stored;
  const id = 'ses_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
  if (typeof window !== 'undefined') window.localStorage.setItem(STORAGE_KEY, id);
  return id;
}

/**
 * Floating chat widget — sales-focused AI assistant.
 * Mounted globally in App.js so it appears on every route.
 */
export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: WELCOME },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [unread, setUnread] = useState(0);
  const [sessionId] = useState(makeSessionId);
  const bodyRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom on new message
  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // Focus input when opened
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 300);
      setUnread(0);
    }
  }, [open]);

  // Nudge: subtle unread bump after 20s if the user hasn't opened the widget
  useEffect(() => {
    if (open) return undefined;
    const t = setTimeout(() => setUnread((u) => (u === 0 ? 1 : u)), 20000);
    return () => clearTimeout(t);
  }, [open]);

  const sendMessage = async (text) => {
    const clean = (text || '').trim();
    if (!clean || loading) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: clean }]);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/chat`, { session_id: sessionId, message: clean }, { timeout: 30000 });
      const reply = (res.data && res.data.message) || 'Sorry — I could not generate a reply. Please try again, or email marketing@ransen.ca.';
      setMessages((m) => [...m, { role: 'assistant', content: reply }]);
      // GTM dataLayer event for engagement tracking
      if (typeof window !== 'undefined' && Array.isArray(window.dataLayer)) {
        window.dataLayer.push({ event: 'chat_message_sent', chat_session_id: sessionId });
      }
    } catch (err) {
      setMessages((m) => [
        ...m,
        {
          role: 'assistant',
          content:
            'Sorry — the assistant is temporarily unavailable. You can reach our team directly at marketing@ransen.ca or +1 (343) 573-2394.',
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const openWidget = () => {
    setOpen(true);
    setMinimized(false);
    setUnread(0);
    if (typeof window !== 'undefined' && Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: 'chat_opened', chat_session_id: sessionId });
    }
  };

  return (
    <>
      {/* Floating trigger button */}
      <button
        type='button'
        onClick={openWidget}
        aria-label='Open chat'
        data-testid='chat-trigger'
        className={`fixed z-[100] bottom-5 right-5 md:bottom-6 md:right-6 flex items-center gap-2 px-4 py-3 rounded-full text-white shadow-2xl shadow-brand/50 border border-white/15 backdrop-blur bg-gradient-to-r from-brand-dark to-brand hover:from-brand hover:to-brand-dark transition-all hover:scale-105 ${
          open ? 'opacity-0 pointer-events-none' : 'opacity-100'
        }`}
      >
        <span className='relative flex items-center justify-center w-7 h-7 rounded-full bg-white/15'>
          <MessageCircle className='w-4 h-4' />
          {unread > 0 && (
            <span className='absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-pink-500 border-2 border-void animate-pulse' />
          )}
        </span>
        <span className='text-sm font-medium hidden sm:inline'>Chat with us</span>
      </button>

      {/* Chat panel */}
      {open && (
        <div
          role='dialog'
          aria-label='Ransen AI Assistant'
          data-testid='chat-panel'
          className={`fixed z-[110] right-3 left-3 sm:left-auto sm:right-5 md:right-6 bottom-3 sm:bottom-5 md:bottom-6 w-auto sm:w-[380px] md:w-[420px] max-w-[420px] rounded-3xl overflow-hidden shadow-2xl shadow-black/60 border border-white/10 flex flex-col ${
            minimized ? 'h-16' : 'h-[600px] max-h-[85vh]'
          } transition-all duration-300`}
          style={{ background: 'linear-gradient(180deg, #100728 0%, #0a0416 100%)' }}
        >
          {/* Header */}
          <header className='shrink-0 flex items-center justify-between gap-3 px-5 py-4 border-b border-white/10 bg-gradient-to-r from-brand-dark/80 to-brand/40'>
            <div className='flex items-center gap-3'>
              <div className='relative w-10 h-10 rounded-full bg-gradient-to-br from-brand to-pink-500 flex items-center justify-center shadow-lg shadow-brand/30'>
                <Sparkles className='w-4.5 h-4.5 text-white' />
                <span className='absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 border-2 border-void' />
              </div>
              <div>
                <div className='text-white font-heading font-semibold text-[15px] leading-tight'>Ransen Assistant</div>
                <div className='text-white/55 text-[11px]'>AI-powered · replies in seconds</div>
              </div>
            </div>
            <button
              type='button'
              onClick={() => setOpen(false)}
              aria-label='Close chat'
              data-testid='chat-close'
              className='w-8 h-8 rounded-full flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-colors'
            >
              <X className='w-4 h-4' />
            </button>
          </header>

          {/* Message body */}
          {!minimized && (
            <>
              <div
                ref={bodyRef}
                className='flex-1 overflow-y-auto px-4 py-5 space-y-3'
                style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.15) transparent' }}
              >
                {messages.map((m, i) => (
                  <Bubble key={i} role={m.role} content={m.content} />
                ))}
                {loading && (
                  <div className='flex justify-start' data-testid='chat-typing'>
                    <div className='max-w-[85%] rounded-2xl rounded-bl-md px-4 py-3 bg-white/[0.06] border border-white/10 flex items-center gap-1'>
                      <span className='w-1.5 h-1.5 rounded-full bg-white/60 animate-bounce' style={{ animationDelay: '0ms' }} />
                      <span className='w-1.5 h-1.5 rounded-full bg-white/60 animate-bounce' style={{ animationDelay: '150ms' }} />
                      <span className='w-1.5 h-1.5 rounded-full bg-white/60 animate-bounce' style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                )}

                {/* Quick prompts (only on first turn) */}
                {messages.length === 1 && !loading && (
                  <div className='pt-2'>
                    <div className='text-white/45 text-[11px] uppercase tracking-wider mb-2 font-accent'>Try one of these</div>
                    <div className='flex flex-wrap gap-2'>
                      {QUICK_PROMPTS.map((p) => (
                        <button
                          key={p}
                          type='button'
                          onClick={() => sendMessage(p)}
                          className='px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10 hover:border-brand/50 hover:bg-brand/10 text-white/75 hover:text-white text-[12.5px] transition-colors'
                          data-testid={`chat-quick-${p.slice(0, 10).replace(/\s+/g, '-').toLowerCase()}`}
                        >
                          {p}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Actions row: book a call / contact links */}
              <div className='shrink-0 px-4 py-2 border-t border-white/5 flex items-center gap-2 flex-wrap'>
                <Link
                  to='/contact'
                  onClick={() => setOpen(false)}
                  className='inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand/15 border border-brand/30 text-brand hover:text-white hover:bg-brand/30 text-[12px] font-medium transition-colors'
                  data-testid='chat-book-call'
                >
                  <Sparkles className='w-3 h-3' /> Book a call
                </Link>
                <a href='mailto:marketing@ransen.ca' className='inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10 hover:border-white/25 text-white/70 hover:text-white text-[12px] transition-colors'>
                  <Mail className='w-3 h-3' /> Email
                </a>
                <a href='tel:+13435732394' className='inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10 hover:border-white/25 text-white/70 hover:text-white text-[12px] transition-colors'>
                  <Phone className='w-3 h-3' /> Call
                </a>
              </div>

              {/* Input */}
              <form onSubmit={onSubmit} className='shrink-0 px-3 pt-2 pb-3 border-t border-white/10 flex items-center gap-2'>
                <input
                  ref={inputRef}
                  type='text'
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder='Ask a question...'
                  disabled={loading}
                  data-testid='chat-input'
                  className='flex-1 px-4 py-2.5 rounded-full bg-white/[0.05] border border-white/10 focus:border-brand/50 focus:bg-white/[0.08] text-white placeholder-white/35 text-[14px] outline-none transition-colors'
                  maxLength={800}
                />
                <button
                  type='submit'
                  disabled={loading || !input.trim()}
                  aria-label='Send'
                  data-testid='chat-send'
                  className='w-10 h-10 rounded-full flex items-center justify-center text-white bg-gradient-to-br from-brand to-pink-500 hover:scale-105 disabled:opacity-40 disabled:hover:scale-100 transition-transform shadow-lg shadow-brand/30'
                >
                  {loading ? <Loader2 className='w-4 h-4 animate-spin' /> : <Send className='w-4 h-4' />}
                </button>
              </form>
            </>
          )}
        </div>
      )}
    </>
  );
}

function Bubble({ role, content }) {
  const isUser = role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`} data-testid={`chat-bubble-${role}`}>
      <div
        className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-[14px] leading-[1.55] font-body ${
          isUser
            ? 'bg-gradient-to-br from-brand to-pink-500 text-white rounded-br-md shadow-lg shadow-brand/25'
            : 'bg-white/[0.06] border border-white/10 text-white/90 rounded-bl-md'
        }`}
      >
        {content.split('\n').map((line, i) => (
          <p key={i} className={i > 0 ? 'mt-2' : ''}>
            {linkify(line)}
          </p>
        ))}
      </div>
    </div>
  );
}

// Turn plain URLs (including ransen.io/... references) into clickable links.
function linkify(text) {
  const parts = [];
  const regex = /(https?:\/\/[^\s)]+|ransen\.io\/[^\s)]+)/g;
  let lastIndex = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    const url = match[0].startsWith('http') ? match[0] : `https://${match[0]}`;
    parts.push(
      <a key={match.index} href={url} target='_blank' rel='noopener noreferrer' className='underline text-brand hover:text-white transition-colors'>
        {match[0]}
      </a>
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts.length ? parts : text;
}
